import type { AuthConfig } from '../types/types'
import type { AccessTokenContactData, Tokens } from '../types/tokenTypes'
import logHandler from '../handlers/logHandler'

// Module scope: every dustinAuth() call creates new handlers, so per-instance state never
// deduplicated anything.
const inflightRefreshes = new Map<string, Promise<Tokens | null>>()

export const resetInflightRefreshes = () => inflightRefreshes.clear()

export const authApiHandler = (config: AuthConfig) => {
  const log = logHandler(config.debug ?? false)

  const getAccessToken = async (
    idToken: string,
    impersonatedContactId?: number
  ): Promise<string | null> => {
    log.info(`Called get access token with contactId: ${impersonatedContactId}`)

    return await fetch(`${getAuthHost()}/getaccesstoken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        impersonatedContactId,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`code: ${response.status} with message: ${response.statusText}`)
        }
        return response.json()
      })
      .then(data => data.micrositeToken)
      .catch((e: Error) => {
        log.error(`Error trying to get access token: ${e}`)
        return null
      })
  }

  const getZendeskToken = async (
    idToken: string,
    impersonatedContactId?: number
  ): Promise<string | null> => {
    log.info(`Called get zendesk token with contactId: ${impersonatedContactId}`)

    return await fetch(`${getAuthHost()}/getzendesktoken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        impersonatedContactId,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`code: ${response.status} with message: ${response.statusText}`)
        }
        return response.json()
      })
      .then(data => data.zendeskToken)
      .catch((e: Error) => {
        log.error(`Error trying to get zendesk token: ${e}`)
        return null
      })
  }

  const getTokens = async (
    code: string,
    provider: string,
    impersonatedContactId?: number
  ): Promise<Tokens | null> => {
    log.info(`Called get tokens with contactId: ${impersonatedContactId}, provider: ${provider}`)

    return await fetch(`${getAuthHost()}/${provider}/gettokens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        impersonatedContactId,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`code: ${response.status} with message: ${response.statusText}`)
        }
        return response.json()
      })
      .catch((e: Error) => {
        log.error(`Error trying to get token: ${e}`)
        return null
      })
  }

  const refreshTokens = async (
    refreshToken: string,
    provider: string,
    contactId?: number
  ): Promise<Tokens | null> => {
    log.info(`Called get refresh tokens with contactId: ${contactId}, provider: ${provider}`)

    // Okta rotates the refresh token on the first call, so concurrent calls with the same
    // token must share one request.
    const host = getAuthHost()
    const key = `${host}|${provider}|${contactId ?? ''}|${refreshToken}`
    const inflight = inflightRefreshes.get(key)
    if (inflight) {
      log.info('Refresh already in-flight, awaiting existing request')
      return inflight
    }

    const request = fetch(`${host}/${provider}/refreshtokens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refreshToken,
        contactId,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`code: ${response.status} with message: ${response.statusText}`)
        }
        return response.json()
      })
      .then((tokens: Tokens) => tokens)
      .catch(() => null)
      .finally(() => {
        inflightRefreshes.delete(key)
      })

    inflightRefreshes.set(key, request)

    return request
  }

  const getAuthHost = () => {
    const host =
      typeof window !== 'undefined' && window.location.hostname !== 'localhost'
        ? `${window.location.origin}/auth`
        : config.authServerUrl

    return host
  }

  const getContacts = async (
    idToken: string,
    impersonatedContactId?: number
  ): Promise<AccessTokenContactData[]> => {
    return await fetch(`${getAuthHost()}/getcontacts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        impersonatedContactId,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`code: ${response.status} with message: ${response.statusText}`)
        }
        return response.json()
      })
      .then(data => data.contacts)
      .catch((e: Error) => {
        log.error(`Error trying to get contacts: ${e}`)
        return []
      })
  }

  return {
    getAccessToken,
    getTokens,
    refreshTokens,
    getContacts,
    getZendeskToken,
  }
}
